import { DataType, FormDialogMode, useNotify } from '@globaleris/ui.library'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AnnexedParameters,
  DocumentTypes,
  EnumCommodity,
  ProcedureStates,
  ProcedureTypes
} from '../../../api/Argentia/enums'
import {
  fetchProcedureItem,
  saveDocumentProcedure
} from '../../../pages/comunicationCenter/tabs/procedures/comunicationProdecures.service'
import { GenericCustomFormDialog } from '../genericCustomFormDialog/genericCustomFormDialog'
import {
  FormStateConfig,
  InputFieldConfig
} from '../genericCustomFormDialog/genericCustomFormDialog.model'
import { enumEnumToValueNumber, enumKeyToValue } from '../../../helpers'
import { ProcedureParameter, ProcedureDocument } from '../../../api/Argentia/endpoints/procedures'

interface ProcedureChangeStateDialogProps {
  procedureId?: number
  procedureState: ProcedureStates
  procedureType: ProcedureTypes
  procedureParameters: ProcedureParameter[]
  commodity?: EnumCommodity
  originProcedureId?: number
  onClose: (formData: any) => void
  open: boolean
}

export const ProcedureChangeStateFormDialog: React.FC<ProcedureChangeStateDialogProps> = ({
  procedureId,
  procedureState,
  procedureType,
  procedureParameters,
  commodity,
  originProcedureId,
  onClose,
  open
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'procedures'])
  const { info, error } = useNotify()
  const [documents, setDocuments] = useState<ProcedureDocument[]>([])

  const changeReasons = ['01', '12', '14', '16', '17', '19', '21', '23']

  const [selectedState, setSelectedState] = useState<number>(0)
  const [showDate, setShowDate] = useState<boolean>(false)

  const EXCLUDED_PROCEDURE_TYPES: Array<ProcedureTypes> = [
    ProcedureTypes.CambioSINCambios,
    ProcedureTypes.ClienteSaliente,
    ProcedureTypes.ClienteSaliente_PWR,
    ProcedureTypes.NotificacionCambios,
    ProcedureTypes.NotificacionModificacionDatos,
    ProcedureTypes.NuevaContratacionAcceso_GAS,
    ProcedureTypes.TraspasoCUR,
    ProcedureTypes.TraspasoCOR,
    ProcedureTypes.Anulacion_Contratacion_GAS,
    ProcedureTypes.CorteSuministroPWR,
    ProcedureTypes.DesistimientoPasivo_PWR,
    ProcedureTypes.DesistimientoPasivo_GAS
  ]

  const PROCEDURE_TYPES_SHOW_DATE: Array<ProcedureTypes> = [
    ProcedureTypes.CambioSINCambios,
    ProcedureTypes.CambioSINCambios_PWR,
    ProcedureTypes.CambioCONCambios_Titular,
    ProcedureTypes.CambioCONCambios_Titular_y_Caudal,
    ProcedureTypes.CambioCONCambios_Ampliacion_Caudal,
    ProcedureTypes.CambioCONCambios_PWR_Adm,
    ProcedureTypes.CambioCONCambios_PWR_Adm_y_Tec,
    ProcedureTypes.CambioCONCambios_PWR_Tec,
    ProcedureTypes.Alta_Nuevo_Suministro,
    ProcedureTypes.Alta_Nuevo_Suministro_PWR,
    ProcedureTypes.Baja_Suministro_GAS,
    ProcedureTypes.Baja_Suministro_PWR,
    ProcedureTypes.ClienteSaliente,
    ProcedureTypes.ClienteSaliente_PWR,
    ProcedureTypes.ReposicionPasiva,
    ProcedureTypes.Modificacion_Titular,
    ProcedureTypes.Modificacion_PWR_Tec,
    ProcedureTypes.Modificacion_PWR_Adm_y_Tec,
    ProcedureTypes.Modificacion_PWR_Adm,
    ProcedureTypes.Reposicion,
    ProcedureTypes.Modificacion_Titular_PWR,
    ProcedureTypes.Modificacion_Titular_y_Tec_PWR
  ]

  useEffect(() => {
    let cancelled = false
    if (
      procedureId !== undefined &&
      procedureState === ProcedureStates.Anulacion_Rechazada &&
      procedureType === ProcedureTypes.CorteSuministroPWR
    ) {
      fetchProcedureItem(procedureId).then(res => {
        if (!cancelled && res.success && res.data) {
          setDocuments(res.data.documentsList ?? [])
        }
      })
    }
    return () => {
      cancelled = true
    }
  }, [procedureId, procedureState, procedureType])

  const allowedStates: Record<
    ProcedureStates,
    (
      commodity: EnumCommodity,
      procedureType: ProcedureTypes,
      reposicionValue: string | undefined
    ) => ProcedureStates[]
  > = {
    [ProcedureStates.Iniciado]: commodity => [
      ProcedureStates.Rechazado,
      ProcedureStates.Aceptado,
      ...(procedureType === ProcedureTypes.Baja_Suministro_GAS ||
      procedureType === ProcedureTypes.CorteSuministroGas ||
      procedureType === ProcedureTypes.Baja_Suministro_PWR ||
      procedureType === ProcedureTypes.CorteSuministroPWR
        ? [ProcedureStates.Anulado]
        : []),
      ...(commodity === EnumCommodity.Electricidad &&
      procedureType !== ProcedureTypes.ClienteSaliente_PWR &&
      procedureType !== ProcedureTypes.ReposicionPasiva &&
      procedureType !== ProcedureTypes.Baja_Suministro_PWR &&
      procedureType !== ProcedureTypes.CorteSuministroPWR
        ? [ProcedureStates.Anulacion_solicitada]
        : [])
    ],
    [ProcedureStates.Rechazado]: (commodity, procedureType, reposicionValue) => {
      if (procedureType === ProcedureTypes.ReposicionPasiva) {
        if (reposicionValue === '03') {
          return [ProcedureStates.Activo, ProcedureStates.AnalisisRechazo]
        }
        if (reposicionValue === '01' || reposicionValue === '02') {
          return []
        }
      }

      return [ProcedureStates.AnalisisRechazo]
    },
    [ProcedureStates.AnalisisRechazo]: () => [ProcedureStates.Descartado],
    [ProcedureStates.Aceptado]: commodity => [
      ProcedureStates.Rechazado,
      ProcedureStates.Activo,
      ...(!EXCLUDED_PROCEDURE_TYPES.includes(procedureType)
        ? [ProcedureStates.PendienteInformacion]
        : []),
      ...(commodity === EnumCommodity.Electricidad &&
      procedureType !== ProcedureTypes.ClienteSaliente_PWR &&
      procedureType !== ProcedureTypes.ReposicionPasiva &&
      procedureType !== ProcedureTypes.Baja_Suministro_PWR &&
      procedureType !== ProcedureTypes.CorteSuministroPWR &&
      procedureType !== ProcedureTypes.DesistimientoPasivo_PWR
        ? [ProcedureStates.Anulacion_solicitada]
        : []),
      ...(procedureType === ProcedureTypes.Baja_Suministro_GAS ||
      procedureType === ProcedureTypes.CorteSuministroGas ||
      procedureType === ProcedureTypes.Baja_Suministro_PWR ||
      procedureType === ProcedureTypes.CorteSuministroPWR
        ? [ProcedureStates.Anulado]
        : [])
    ],
    [ProcedureStates.PendienteInformacion]: commodity => [
      ProcedureStates.Aceptado,
      ...(commodity === EnumCommodity.Electricidad &&
      procedureType !== ProcedureTypes.Baja_Suministro_PWR &&
      procedureType !== ProcedureTypes.CorteSuministroPWR
        ? [ProcedureStates.Anulacion_solicitada]
        : []),
      ...(procedureType === ProcedureTypes.Baja_Suministro_GAS ||
      procedureType === ProcedureTypes.CorteSuministroGas ||
      procedureType === ProcedureTypes.Baja_Suministro_PWR ||
      procedureType === ProcedureTypes.CorteSuministroPWR
        ? [ProcedureStates.Anulado]
        : [])
    ],
    [ProcedureStates.Anulacion_solicitada]: () => [
      ProcedureStates.Anulacion_Rechazada,
      ProcedureStates.Anulado
    ],
    [ProcedureStates.Anulacion_Rechazada]: () => {
      const isSuspensionPwr = procedureType === ProcedureTypes.CorteSuministroPWR
      const isSuspensionGas = procedureType === ProcedureTypes.CorteSuministroGas

      if (isSuspensionPwr) {
        const docs = documents.filter(d => {
          const docTypeValue = enumEnumToValueNumber(DocumentTypes, d.type)
          const docStateValue = enumEnumToValueNumber(ProcedureStates, d.state)

          return (
            (docTypeValue === DocumentTypes.Paso_02 &&
              docStateValue === ProcedureStates.Aceptado) ||
            (docTypeValue === DocumentTypes.Paso_Manual &&
              docStateValue === ProcedureStates.Aceptado)
          )
        })

        if (docs.length > 0) {
          return [ProcedureStates.AnalisisRechazo]
        }
      }

      return [
        ProcedureStates.Rechazado,
        ProcedureStates.Aceptado,
        ProcedureStates.Activo,
        ...(!isSuspensionPwr ? [ProcedureStates.PendienteInformacion] : []),
        ...(procedureType !== ProcedureTypes.Baja_Suministro_PWR && !isSuspensionPwr
          ? [ProcedureStates.Anulacion_solicitada]
          : []),
        // show AnalisisRechazo only for Suspension GAS
        ...(isSuspensionGas ? [ProcedureStates.AnalisisRechazo] : [])
      ]
    },
    [ProcedureStates.Bloqueado]: () => [ProcedureStates.Iniciado, ProcedureStates.Descartado],
    [ProcedureStates.Pendiente_Respuesta]: () => [],
    [ProcedureStates.Incidencia]: () => [],
    [ProcedureStates.Pendiente_Activacion]: () => [],
    [ProcedureStates.Activo]: () => [
      ...(procedureType === ProcedureTypes.CorteSuministroGas ||
      procedureType === ProcedureTypes.CorteSuministroPWR
        ? [ProcedureStates.Anulado]
        : [])
    ],
    [ProcedureStates.Cerrado]: () => [],
    [ProcedureStates.Informacion_Adicional_Enviada]: () => [],
    [ProcedureStates.Pendiente_Envio]: () => [],
    [ProcedureStates.Anulado]: () => [],
    [ProcedureStates.Descartado]: () => []
  }

  const shouldShowDate = (
    value: any,
    procedureType: ProcedureTypes,
    originProcedureState: ProcedureStates | undefined,
    procedureTypeValue: number | undefined,
    changeReasonValue: string | undefined,
    terminationReasonValue: string | undefined,
    isDesistimientoPasivo: boolean,
    isDesistimiento: boolean,
    changeReasonPWRValue: string | undefined,
    isAnulable: boolean
  ): boolean => {
    if (
      (procedureType === ProcedureTypes.Modificacion_Titular_PWR ||
        procedureType === ProcedureTypes.Modificacion_Titular_y_Tec_PWR) &&
      value === ProcedureStates.Aceptado &&
      changeReasonPWRValue === 'S'
    ) {
      return true
    }

    if (value !== ProcedureStates.Activo) {
      return false
    }

    const isNotTerminationReason03 =
      PROCEDURE_TYPES_SHOW_DATE.includes(procedureType) && terminationReasonValue !== '03'

    const isDesistimientoNoAnulable =
      isDesistimiento && originProcedureState === ProcedureStates.Activo

    const isDesistimientoPasivoNoAnulable = isDesistimientoPasivo && !isAnulable

    const isChangeOfOwnerGas =
      changeReasons.includes(changeReasonValue ?? '') &&
      procedureTypeValue === ProcedureTypes.Modificacion_GAS_Adm

    const isSuspensionPWR =
      procedureType === ProcedureTypes.CorteSuministroPWR &&
      procedureState === ProcedureStates.Aceptado

    return (
      isNotTerminationReason03 ||
      isDesistimientoNoAnulable ||
      isChangeOfOwnerGas ||
      isSuspensionPWR ||
      isDesistimientoPasivoNoAnulable
    )
  }

  const handleStateChange = async (value: any) => {
    if (value === ProcedureStates.Activo || value === ProcedureStates.Aceptado) {
      let originProcedureState: number | undefined
      let isDesistimientoPasivo =
        procedureType === ProcedureTypes.DesistimientoPasivo_PWR ||
        procedureType === ProcedureTypes.DesistimientoPasivo_GAS

      const isDesistimiento =
        procedureType === ProcedureTypes.Desistimiento_PWR ||
        procedureType === ProcedureTypes.Desistimiento_GAS

      if (isDesistimiento && originProcedureId !== undefined) {
        const response = await fetchProcedureItem(originProcedureId)
        if (response.success && response.data) {
          originProcedureState = enumEnumToValueNumber(ProcedureStates, response.data.state)
        }
      }

      const procedureTypeValue = enumEnumToValueNumber(ProcedureTypes, procedureType)

      //Solo mostrar fecha para cambios de titular (MOTIVO_MODIFICACION GAS)
      const changeReasonValue = getAnnexedParam(
        procedureParameters,
        AnnexedParameters.MOTIVO_MODIFICACION
      )?.value
      const terminationReasonValue = getAnnexedParam(
        procedureParameters,
        AnnexedParameters.MOTIVO_BAJA
      )?.value

      const changeReasonPWRValue = getAnnexedParam(
        procedureParameters,
        AnnexedParameters.MOTIVO_MODIFICACION_PWR
      )?.value

      const isAnulable =
        getAnnexedParam(procedureParameters, AnnexedParameters.IND_ANULABLE)?.value === 'S'

      const newShowDate = shouldShowDate(
        value,
        procedureType,
        originProcedureState,
        procedureTypeValue,
        changeReasonValue,
        terminationReasonValue,
        isDesistimientoPasivo,
        isDesistimiento,
        changeReasonPWRValue,
        isAnulable
      )

      setShowDate(newShowDate)
    } else {
      setShowDate(false)
    }

    setSelectedState(value)
  }

  const getStateList = (
    t: any,
    state: ProcedureStates,
    commodity: EnumCommodity,
    procedureType: ProcedureTypes
  ) => {
    let reposicionTypeValue

    if (procedureType === ProcedureTypes.ReposicionPasiva) {
      reposicionTypeValue = getAnnexedParam(
        procedureParameters,
        AnnexedParameters.TIPO_REPOSICION
      )?.value
    }

    const states = allowedStates[state]
      ? allowedStates[state](commodity, procedureType, reposicionTypeValue)
      : []

    return states.map(s => ({
      value: s,
      label: t(`enums:ProcedureStates.${ProcedureStates[s]}`)
    }))
  }

  const getAnnexedParam = (parameters: ProcedureParameter[], paramKey: AnnexedParameters) => {
    return parameters?.find(param => enumKeyToValue(AnnexedParameters, param.type) === paramKey)
  }

  const stateList = getStateList(t, procedureState, commodity!, procedureType)

  const inputFields: InputFieldConfig[] = [
    {
      name: 'state',
      label: t('procedures:components.procedureRejectFormDialog.inputs.state'),
      type: DataType.SELECT,
      selectOptions: stateList,
      required: true,
      defaultValue: selectedState,
      onChange: handleStateChange
    },
    {
      name: 'date',
      label: t('procedures:components.procedureRejectFormDialog.inputs.date'),
      type: DataType.DATE,
      display: showDate,
      required: showDate
    }
  ]

  const formStateConfig: FormStateConfig = {
    formMode: FormDialogMode.ADD,
    open,
    inputFields
  }

  const handleClose = async (formData: any) => {
    onClose(formData)
    setShowDate(false)
    setSelectedState(0)
    if (formData) {
      const newProcedure = {
        idProcedure: procedureId,
        type: DocumentTypes.Paso_Manual,
        state: formData.state,
        date: moment().toDate(),
        url: '.',
        parameters: formData.date
          ? [
              {
                type: AnnexedParameters.FH_EFECTO,
                value: moment(formData.date).format('YYYY-MM-DD') ?? ''
              }
            ]
          : []
      }
      await saveDocumentProcedure(newProcedure).then(response => {
        if (response.success) {
          info(
            t(
              'procedures:components.annexedParamCustomFormDialog.notifications.success.createProcedureDocument'
            )
          )
        } else {
          error(
            t(
              'procedures:components.annexedParamCustomFormDialog.notifications.error.createProcedureDocument'
            )
          )
        }
      })
    }
  }

  return (
    <GenericCustomFormDialog
      formStateConfig={formStateConfig}
      data={procedureId}
      onClose={handleClose}
      title={t('procedures:components.procedureRejectFormDialog.title')}
    />
  )
}
